import { Kegiatan, Santri, TempatPresensi } from '../types';
import { isKegiatanOpenForAbsen } from './kegiatan';

/**
 * Mode Kiosk Presensi Kegiatan (docs/PANDUAN_KIOSK_PRESENSI.md).
 * Fungsi murni di sini dipakai halaman /kiosk dan diuji tanpa React.
 */

export const KIOSK_PERMISSION_ID = 'pg-kiosk-presensi';
export const KIOSK_PATH = '/kiosk';

// Menu yang tidak dihitung saat menentukan "akun khusus kiosk".
const MENU_DASAR = ['portal-guru'];

/** Akun yang (selain akses dasar) hanya diberi menu Kiosk Presensi. */
export const isKioskOnlyMenus = (allowedMenus: string[]): boolean => {
  if (!allowedMenus.includes(KIOSK_PERMISSION_ID)) return false;
  return allowedMenus.every(m => m === KIOSK_PERMISSION_ID || MENU_DASAR.includes(m));
};

// ---------------------------------------------------------------------------
// Penanda perangkat kiosk (disimpan di perangkat, bukan di akun)
// ---------------------------------------------------------------------------

const DEVICE_KEY = 'kioskPresensiPerangkat';

export interface KioskDevice {
  tempatId: string;
  tempatNama: string;
  dipasangPada: string;
}

export const getKioskDevice = (): KioskDevice | null => {
  try {
    const raw = localStorage.getItem(DEVICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.tempatId ? parsed as KioskDevice : null;
  } catch {
    return null;
  }
};

export const setKioskDevice = (tempat: Pick<TempatPresensi, 'id' | 'nama'>) => {
  try {
    const data: KioskDevice = { tempatId: tempat.id, tempatNama: tempat.nama, dipasangPada: new Date().toISOString() };
    localStorage.setItem(DEVICE_KEY, JSON.stringify(data));
  } catch {
    // Penyimpanan perangkat tidak tersedia (mode privat); kiosk tetap jalan selama halaman terbuka.
  }
};

export const clearKioskDevice = () => {
  try {
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    // abaikan
  }
};

// ---------------------------------------------------------------------------
// Jadwal
// ---------------------------------------------------------------------------

/** Kegiatan yang bisa tampil di kiosk tempat ini (parenting tidak ikut, lihat dokumentasi). */
export const getKegiatanDiTempat = (kegiatanList: Kegiatan[], tempatId: string): Kegiatan[] =>
  kegiatanList.filter(k => k.isActive && !k.isParenting && (k.tempat || []).includes(tempatId));

/** Kegiatan di tempat ini yang presensinya sedang dibuka. */
export const getKegiatanAktifKiosk = (kegiatanList: Kegiatan[], tempatId: string, now: Date = new Date()): Kegiatan[] =>
  getKegiatanDiTempat(kegiatanList, tempatId)
    .filter(k => isKegiatanOpenForAbsen(k, now))
    .sort((a, b) => a.jamMulai.localeCompare(b.jamMulai) || a.namaKegiatan.localeCompare(b.namaKegiatan));

const NAMA_HARI = ['Ahad', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

const setJam = (tanggal: Date, jam: string): Date => {
  const [h, m] = jam.split(':').map(Number);
  const d = new Date(tanggal);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Jam mulai kegiatan pada tanggal `ref` (dipakai aturan "jam mulai terdekat"). */
export const getJamMulaiPada = (kegiatan: Kegiatan, ref: Date): Date => setJam(ref, kegiatan.jamMulai);

/**
 * Pembukaan presensi berikutnya di tempat ini (jam mulai − "absen bisa dimulai sebelum"),
 * dicari sampai 7 hari ke depan.
 */
export const getJadwalBerikutnya = (
  kegiatanList: Kegiatan[],
  tempatId: string,
  now: Date = new Date()
): { kegiatan: Kegiatan; dibukaPada: Date } | null => {
  let terbaik: { kegiatan: Kegiatan; dibukaPada: Date } | null = null;

  getKegiatanDiTempat(kegiatanList, tempatId).forEach(k => {
    const majuMenit = Number(k.absenBisaDimulaiSebelum) || 0;
    for (let i = 0; i <= 7; i++) {
      const hari = startOfDay(now);
      hari.setDate(hari.getDate() + i);

      if (k.jenis === 'Rutin') {
        if (!k.hari?.includes(NAMA_HARI[hari.getDay()])) continue;
      } else {
        // Non Rutin: presensi dibuka sekali, pada tanggal mulai.
        if (!k.tanggalMulai || startOfDay(new Date(k.tanggalMulai)).getTime() !== hari.getTime()) continue;
      }

      const dibukaPada = new Date(setJam(hari, k.jamMulai).getTime() - majuMenit * 60000);
      if (dibukaPada <= now) continue;
      if (!terbaik || dibukaPada < terbaik.dibukaPada) terbaik = { kegiatan: k, dibukaPada };
      break;
    }
  });

  return terbaik;
};

// ---------------------------------------------------------------------------
// Pengarahan scan
// ---------------------------------------------------------------------------

/** Santri sesuai gender tempat (tempat Campur menerima semua). */
export const isSantriSesuaiTempat = (s: Santri, gender: TempatPresensi['gender']): boolean => {
  if (gender === 'Putra') return s.jenisKelamin === 'Laki-laki';
  if (gender === 'Putri') return s.jenisKelamin === 'Perempuan';
  return true;
};

/** Cari santri dari isi kartu RFID / QR (UID kartu, NIS, atau id santri). */
export const cariSantriDariKartu = (kode: string, daftar: Santri[]): Santri | undefined => {
  const tag = kode.trim().toUpperCase();
  if (!tag) return undefined;
  return daftar.find(s =>
    (s.rfid && s.rfid.toUpperCase() === tag) ||
    (s.nis && s.nis.replace(/\D/g, '') === tag) ||
    s.id.toUpperCase() === tag
  );
};

/**
 * Pilih satu kegiatan bila santri menjadi peserta beberapa kegiatan aktif
 * sekaligus: aturan "jam mulai terdekat" dengan waktu scan.
 */
export const pilihKegiatanTerdekat = (kandidat: Kegiatan[], now: Date = new Date()): Kegiatan | undefined => {
  if (kandidat.length <= 1) return kandidat[0];
  return [...kandidat].sort((a, b) => {
    const da = Math.abs(getJamMulaiPada(a, now).getTime() - now.getTime());
    const db = Math.abs(getJamMulaiPada(b, now).getTime() - now.getTime());
    return da - db || a.namaKegiatan.localeCompare(b.namaKegiatan);
  })[0];
};
